import { useRef, useState } from 'react';
import { newSubmissionId, signUpload, uploadToCloudinary, verifyPhoto } from '../lib/api.js';
import { enqueue, isOnline } from '../lib/submissionQueue.js';
import { compressForUpload, shouldCompress } from '../lib/imageCompression.js';

// Tap-to-pick → Cloudinary direct upload → photo-verify call.
// On iOS Safari we deliberately do NOT set the `capture` attribute:
// most real trip submissions are DSLR transfers in the Photos roll,
// and `capture` would force direct-camera capture and skip the library.
// Without it, iOS shows the native chooser (Photo Library / Take Photo /
// Choose File) and EXIF is preserved on library picks.

export default function PhotoUploader({
  tripId,
  playerId,
  challengeId,
  testMode,
  onVerified,
  caption, // optional companion text (used by photo_and_text)
}) {
  const inputRef = useRef(null);
  const [stage, setStage] = useState('idle'); // idle | compressing | signing | uploading | verifying | queued | done | error
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  async function handleFile(rawFile) {
    if (!rawFile) return;
    setError(null);
    setProgress(0);
    setPreviewUrl(URL.createObjectURL(rawFile));

    // Compress before anything else — Cloudinary free tier rejects single
    // uploads >10 MB and DSLR transfers commonly exceed that. EXIF is
    // preserved through the compression. Skipped silently for files
    // already under 2 MB.
    let file = rawFile;
    if (shouldCompress(rawFile)) {
      setStage('compressing');
      file = await compressForUpload(rawFile);
    }

    if (!isOnline()) {
      await queueOffline(file);
      return;
    }

    const submissionId = newSubmissionId();
    try {
      setStage('signing');
      const signed = await signUpload({ tripId, challengeId, submissionId });

      setStage('uploading');
      const uploaded = await uploadToCloudinary({
        file,
        signed,
        onProgress: (p) => setProgress(p),
      });

      setStage('verifying');
      const result = await verifyPhoto({
        tripId,
        challengeId,
        submissionId,
        photoUrl: uploaded.secure_url,
        timestamp: new Date().toISOString(),
        testMode,
        exif: uploaded.image_metadata || null,
      });

      setStage('done');
      onVerified?.({ ...result, caption });
    } catch (err) {
      // If the user went offline mid-flight, drop into the queue rather
      // than surfacing a "fetch failed" error.
      if (!isOnline() || isLikelyNetworkError(err)) {
        await queueOffline(file, err);
        return;
      }
      setStage('error');
      setError(err.message || String(err));
    }
  }

  async function queueOffline(file, originalErr) {
    try {
      await enqueue({
        trip_id: tripId,
        player_id: playerId,
        challenge_id: challengeId,
        submission_type: caption ? 'photo_and_text' : 'photo',
        files: [
          {
            name: file.name || 'photo.jpg',
            type: file.type || 'image/jpeg',
            blob: file,
          },
        ],
        text: caption || null,
        timestamp_claimed: new Date().toISOString(),
        test_mode: !!testMode,
      });
      setStage('queued');
    } catch (err) {
      setStage('error');
      setError(
        `Could not queue offline: ${err.message || String(err)}` +
          (originalErr ? ` (original: ${originalErr.message || originalErr})` : '')
      );
    }
  }

  const busy = ['compressing', 'signing', 'uploading', 'verifying'].includes(stage);

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {previewUrl && (
        <img
          src={previewUrl}
          alt="submission preview"
          className="h-44 w-full rounded-md border border-border-tertiary object-cover"
        />
      )}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="flex h-12 w-full items-center justify-center rounded-md border border-action bg-action font-mono text-xs uppercase tracking-wider text-action-fg transition hover:bg-action-hover disabled:opacity-60"
      >
        {stage === 'idle' && 'tap to capture / pick photo'}
        {stage === 'compressing' && 'compressing…'}
        {stage === 'signing' && 'signing upload…'}
        {stage === 'uploading' && `uploading… ${Math.round(progress * 100)}%`}
        {stage === 'verifying' && 'awaiting verdict…'}
        {stage === 'queued' && 'queued · uploads when signal returns'}
        {stage === 'done' && 'submit another'}
        {stage === 'error' && 'retry'}
      </button>

      {stage === 'queued' && (
        <p className="rounded-md border border-copper/40 bg-copper/10 px-3 py-2 font-mono text-[11px] text-copper-dark">
          Held offline. The Wilman tab&apos;s queue will drain when signal returns.
        </p>
      )}

      {error && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function isLikelyNetworkError(err) {
  if (!err) return false;
  const msg = String(err.message || err).toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('offline') ||
    msg.includes('timed out')
  );
}
